// The content as it travels: the content's JSON files and tiles.json, a count then each
// name and its UTF-8 text, all deflated. A world sends it to a client whose fingerprint differs; the client keeps the
// last one (cacheName) and starts with it next time.
import { InvalidDataError, NetReader, NetWriter, deflate, inflate, utf8 } from './bytes';
import { GameContent } from './content';
import type { ClientStorage } from './storage';
import { log } from './log';

export const cacheName = 'content/latest.bin';
const maxBytes = 16 * 1024 * 1024;
const decoder = new TextDecoder();

export async function decodeBundle(encoded: Uint8Array): Promise<Map<string, string>> {
  const raw = await inflate(encoded, maxBytes);
  const r = new NetReader(raw);
  const files = new Map<string, string>();
  const count = r.ushort();
  for (let i = 0; i < count; i++) {
    const name = r.string(64);
    const length = r.int();
    if (length < 0 || length > r.available) throw new InvalidDataError('content bundle cut short');
    files.set(name, decoder.decode(r.bytes(length)));
  }
  return files;
}

export async function encodeBundle(files: ReadonlyMap<string, string>): Promise<Uint8Array> {
  const w = new NetWriter().ushort(files.size);
  for (const [name, text] of [...files].sort(([a], [b]) => (a < b ? -1 : 1))) {
    const bytes = utf8(text);
    w.string(name).int(bytes.length).bytes(bytes);
  }
  return deflate(w.toBytes());
}

/** The content a world last sent (kept in storage), or null if there's none or it's damaged. */
export async function cachedContent(storage: ClientStorage): Promise<GameContent | null> {
  const bundle = storage.read(cacheName);
  if (!bundle) return null;
  try {
    return new GameContent(await decodeBundle(bundle));
  } catch (e) {
    log.warn(`Ignoring the saved content: ${(e as Error).message}`);
    return null;
  }
}
